// Per-invoice payment status helpers used across the Payments page and the
// project / customer detail views.
//
// An invoice cycles through:
//   not-issued — no invoice_date yet
//   pending    — issued, not paid, due_date in the future ("Henüz gelmedi")
//   overdue    — issued, not paid, due_date in the past ("Gecikmede")
//   paid       — verified invoice balance is zero
//
// The legacy financials.paid_amount column is the SUM of paid_amount_1 +
// paid_amount_2; the per-invoice split is the source of truth.

// Liongate convention: both invoices are due on the day they're raised
// (stage-payment model, expected immediately).
export const DEFAULT_PAYMENT_TERMS_DAYS = 0;
export const DEFAULT_INV2_SCHEDULE_MONTHS = 3;

// Half-penny tolerance for decimal rounding; never forgive a whole pound.
export const PENDING_THRESHOLD = 0.005;

export function defaultDueDate(invoiceDate, daysOffset = DEFAULT_PAYMENT_TERMS_DAYS) {
  if (!invoiceDate) return null;
  const d = new Date(invoiceDate);
  if (isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + daysOffset);
  return d.toISOString().slice(0, 10);
}

export function effectiveDueDate(invoiceDate, dueDate, daysOffset) {
  return dueDate || defaultDueDate(invoiceDate, daysOffset);
}

// Add n months to a YYYY-MM-DD date string, returning the same format.
function addMonths(isoDate, months) {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return null;
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + months);
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, last));
  return d.toISOString().slice(0, 10);
}

// UTC midnight of "today" — for date-only comparison so an invoice due today
// counts as Pending (Due today), not Overdue.
function todayUtcMidnight() {
  const t = new Date();
  return Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate());
}

export function invoiceStatus({ invoice_no, invoice_date, due_date, paid_amount, amount, daysOffset }) {
  // An invoice counts as "issued" if either a number or a date is recorded —
  // Cagri sometimes enters one without the other.
  const issued = !!(invoice_no || invoice_date);
  const paid = Number(paid_amount) || 0;
  if (!issued && paid<=0) return 'not-issued';
  // An unverified invoice total must not be inferred from the project fee.
  if (amount == null || amount === '') return paid>0?'payment-recorded':'unverified';
  const remaining = Math.max(0, Number(amount) - paid);
  if (remaining < PENDING_THRESHOLD) return 'paid';
  const dd = effectiveDueDate(invoice_date, due_date, daysOffset);
  if (!dd) return paid > 0 ? 'partial' : 'pending';
  return new Date(dd).getTime() < todayUtcMidnight() ? 'overdue' : (paid > 0 ? 'partial' : 'pending');
}

export function daysOverdue(due_date) {
  if (!due_date) return 0;
  const dt = new Date(due_date);
  if (isNaN(dt.getTime())) return 0;
  return Math.max(0, Math.floor((Date.now() - dt.getTime()) / 86400000));
}

export function projectPaymentSummary(project) {
  const f = project?.financials?.[0];
  const empty = {
    agreed: 0, paid: 0, pending: 0, overdue: 0, hasFinancials: false,
    inv1: { status: 'not-issued' }, inv2: { status: 'not-issued' },
  };
  if (!f) return empty;

  const agreed = Number(f.agreed_fee) || 0;
  const paid1 = Number(f.paid_amount_1) || 0;
  const paid2 = Number(f.paid_amount_2) || 0;
  // Fall back to legacy paid_amount for rows that haven't been backfilled yet.
  const paidLegacy = Number(f.paid_amount) || 0;
  const paid = (paid1 + paid2) || paidLegacy;

  let inv1Status = invoiceStatus({
    invoice_no: f.invoice_no_1,
    invoice_date: f.invoice_date_1, due_date: f.invoice_due_date_1,
    paid_amount: paid1 || (paidLegacy && !f.invoice_no_2 ? paidLegacy : 0), amount: f.invoice_amount_1,
  });
  let inv2Status = invoiceStatus({
    invoice_no: f.invoice_no_2,
    invoice_date: f.invoice_date_2, due_date: f.invoice_due_date_2,
    paid_amount: paid2, amount: f.invoice_amount_2,
  });

  const noSecondEvidence=!(f.invoice_no_2||f.invoice_date_2||paid2>0);
  const noSecond=noSecondEvidence&&agreed>0&&((f.invoice_amount_2!=null&&f.invoice_amount_2!==''&&Number(f.invoice_amount_2)===0)||Number(f.invoice_amount_1)>=agreed);
  const fullyPaid=agreed>0&&paid>=agreed-PENDING_THRESHOLD;
  if(fullyPaid){inv1Status='paid';inv2Status='paid';}
  if(noSecond)inv2Status='not-required';
  const invoicePaid1 = paid1 || (paidLegacy && !f.invoice_no_2 ? paidLegacy : 0);
  let overdue = 0;
  if (inv1Status === 'overdue') overdue += Math.max(0, Number(f.invoice_amount_1) - invoicePaid1);
  if (inv2Status === 'overdue') overdue += Math.max(0, Number(f.invoice_amount_2) - paid2);

  // Expected inv 2 issue date = inv 1 date + project's schedule (default 3 mo).
  // We only prompt "Issue invoice" on inv 2 once this date has arrived.
  const scheduleMonths = Number(f.inv2_schedule_months) || DEFAULT_INV2_SCHEDULE_MONTHS;
  const inv2Expected = addMonths(f.invoice_date_1, scheduleMonths);
  const inv2TimeToIssue = !!inv2Expected && new Date(inv2Expected).getTime() <= Date.now();

  const rawPending = Math.max(0, agreed - paid);
  const pending = rawPending > 0 && rawPending < PENDING_THRESHOLD ? 0 : rawPending;
  return {
    agreed,
    paid,
    pending,
    overdue,
    hasFinancials: true,
    inv1: {
      amount:f.invoice_amount_1, status: inv1Status, paid: paid1||(!f.invoice_no_2?paidLegacy:null)||null, projectPaid:fullyPaid&&!paid1, date: f.invoice_date_1,
      due: f.invoice_due_date_1, no: f.invoice_no_1,
    },
    inv2: {
      amount:f.invoice_amount_2, status: inv2Status, paid: paid2||null, projectPaid:fullyPaid&&!paid2, date: f.invoice_date_2,
      due: f.invoice_due_date_2, no: f.invoice_no_2,
      expectedIssueDate: noSecond?null:inv2Expected,
      isTimeToIssue: !noSecond&&inv2TimeToIssue,
      scheduleMonths,
    },
  };
}

// One-word rollup used for filters and the rightmost Payment column.
export function projectAggregateStatus(project) {
  const s = projectPaymentSummary(project);
  if (!s.agreed) return { key: 'none', label: '—' };
  // Disputed is a project-level state; in payments terms it's closed.
  if (project?.status === 'Disputed') return { key: 'disputed', label: 'Disputed' };
  if (s.overdue > 0)      return { key: 'overdue', label: 'Overdue' };
  // pending = 0 (after the rounding threshold) AND paid > 0 → effectively paid.
  if (s.pending === 0 && s.paid > 0) return { key: 'paid', label: 'Paid' };
  if (s.paid >= s.agreed) return { key: 'paid',    label: 'Paid' };
  if (s.paid > 0)         return { key: 'partial', label: 'Partial' };
  if (s.inv1.status === 'not-issued' && s.inv2.status === 'not-issued') {
    return { key: 'unbilled', label: 'Unbilled' };
  }
  return { key: 'pending', label: 'Pending' };
}

// Display style for an invoice status badge.
export function invoiceStatusStyle(status) {
  switch (status) {
    case 'not-required': return {color:'var(--text3)',bg:'var(--bg3)',label:'Not required'};
    case 'payment-recorded': return {color:'var(--green)',bg:'var(--green-dim)',label:'Payment recorded'};
    case 'unverified': return { color: 'var(--orange)', bg: 'var(--orange-dim)', label: 'Verify amount' };
    case 'partial': return { color: 'var(--orange)', bg: 'var(--orange-dim)', label: 'Partial' };
    case 'paid':       return { color: 'var(--green)',  bg: 'var(--green-dim)',  label: 'Paid' };
    case 'overdue':    return { color: 'var(--red)',    bg: 'var(--red-dim)',    label: 'Overdue' };
    case 'pending':    return { color: 'var(--blue)',   bg: 'var(--blue-dim)',   label: 'Pending' };
    case 'not-issued': return { color: 'var(--text3)',  bg: 'var(--bg3)',        label: 'Not issued' };
    default:           return { color: 'var(--text3)',  bg: 'var(--bg3)',        label: '—' };
  }
}

export function aggregateStatusStyle(key) {
  switch (key) {
    case 'paid':     return { color: 'var(--green)',  bg: 'var(--green-dim)' };
    case 'overdue':  return { color: 'var(--red)',    bg: 'var(--red-dim)' };
    case 'pending':  return { color: 'var(--blue)',   bg: 'var(--blue-dim)' };
    case 'partial':  return { color: 'var(--yellow)', bg: 'var(--yellow-dim)' };
    case 'disputed': return { color: 'var(--orange)', bg: 'var(--orange-dim)' };
    case 'unbilled': return { color: 'var(--text3)',  bg: 'var(--bg3)' };
    default:         return { color: 'var(--text3)',  bg: 'var(--bg3)' };
  }
}
